import React from "react";
import { useNavigate } from "react-router-dom";
import { MtdMetricCard } from "./MtdMetricCard";
import { ThisMonthInProgressSkeleton } from "../shared/ThisMonthInProgressSkeleton";
import type { MtdMetricCardProps } from "./MtdMetricCard";
import { useThisMonthInProgress } from "../../hooks/useThisMonthInProgress";

export function ThisMonthInProgressSection() {
  const navigate = useNavigate();
  const data = useThisMonthInProgress();

  const monthTitle = data.monthTitle;
  const asOfLabel = data.asOfLabel;
  const balancesAsOfLabel = data.balancesAsOfLabel;
  const firstRow = data.mtdCardsFirstRow;
  const secondRow = data.mtdCardsSecondRow;

  const getMetricRoute = (label: string): string | null => {
    switch (label.trim().toLowerCase()) {
      case "revenue mtd":
        return "/revenue";
      case "expense mtd":
      case "expenses mtd":
        return "/expense";
      case "cash balance":
        return "/cash-and-bank";
      case "receivables":
        return "/ar-aging";
      case "payables":
        return "/ap-aging";
      default:
        return null;
    }
  };

  const renderCards = (cards: MtdMetricCardProps[]) =>
    cards.map((card, i) => {
      const route = getMetricRoute(card.label);
      return (
        <MtdMetricCard
          key={`${card.label}-${i}`}
          {...card}
          onClick={route ? () => navigate(route) : undefined}
        />
      );
    });

  if (data.loading) {
    return <ThisMonthInProgressSkeleton />;
  }

  if (data.error) {
    return (
      <div className="flex w-full flex-col items-start gap-4">
        <span className="text-caption-bold font-caption-bold text-neutral-500 uppercase tracking-widest">
          This Month in Progress
        </span>
        <div className="w-full rounded-lg bg-error-50 px-4 py-2" role="alert">
          <span className="text-caption font-caption text-error-600">
            {data.error}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <span className="text-caption-bold font-caption-bold text-neutral-500 uppercase tracking-widest">
        This Month in Progress
      </span>

      {/* MTD only: month title, effective as-of, Revenue & Expense */}
      <div className="flex w-full flex-col items-start rounded-xl bg-white shadow-md">
        <div className="flex w-full items-end justify-between px-6 pt-6 pb-4">
          <span className="text-heading-2 font-heading-2 text-neutral-950 uppercase tracking-wider">
            {monthTitle}
          </span>
          <span className="text-caption font-caption text-neutral-400">
            {asOfLabel}
          </span>
        </div>
        <div className="flex w-full flex-wrap gap-4 px-6 pb-8 items-stretch">
          {renderCards(firstRow)}
        </div>
      </div>

      {/* Cash, receivables, payables — separate card */}
      <div className="flex w-full flex-col items-start rounded-xl bg-white shadow-md">
        <div className="flex w-full items-end justify-end px-6 pt-6 pb-4">
          <span className="text-caption font-caption text-neutral-400">
            {balancesAsOfLabel}
          </span>
        </div>
        <div className="flex w-full flex-wrap gap-4 px-6 pb-8 items-stretch">
          {renderCards(secondRow)}
        </div>
      </div>
    </div>
  );
}
